import type { StylesConfig } from 'react-select';

/**
 * Returns dark-mode-aware styles for react-select components.
 * Detects dark mode via Tailwind's `dark` class on the document element.
 */
export function getReactSelectStyles<Option = unknown, IsMulti extends boolean = boolean>(): StylesConfig<Option, IsMulti> {
    const isDark = document.documentElement.classList.contains('dark');

    return {
        control: (base, state) => ({
            ...base,
            backgroundColor: isDark ? '#1e293b' : '#fff', // slate-800 / white
            borderColor: state.isFocused
                ? '#22c55e' // green-500
                : isDark ? 'rgba(255,255,255,0.1)' : '#d1d5db', // white/10 / gray-300
            boxShadow: state.isFocused ? '0 0 0 1px #22c55e' : base.boxShadow,
            '&:hover': {
                borderColor: state.isFocused
                    ? '#22c55e'
                    : isDark ? 'rgba(255,255,255,0.2)' : '#9ca3af', // gray-400
            },
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: isDark ? '#1e293b' : '#fff',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : base.border,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? isDark ? 'rgba(34,197,94,0.2)' : '#dcfce7' // green-900/20 / green-100
                : state.isFocused
                ? isDark ? '#334155' : '#f3f4f6' // slate-700 / gray-100
                : 'transparent',
            color: isDark ? '#f1f5f9' : '#111827', // gray-100 / gray-900
            '&:active': {
                backgroundColor: isDark ? '#334155' : '#e5e7eb',
            },
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: isDark ? 'rgba(34,197,94,0.2)' : '#dcfce7', // green-900/20 / green-100
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: isDark ? '#86efac' : '#15803d', // green-300 / green-700
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: isDark ? '#86efac' : '#15803d',
            '&:hover': {
                backgroundColor: '#ef4444', // red-500
                color: '#fff',
            },
        }),
        placeholder: (base) => ({
            ...base,
            color: isDark ? '#6b7280' : '#9ca3af', // gray-500 / gray-400
        }),
        input: (base) => ({
            ...base,
            color: isDark ? '#f1f5f9' : '#111827', // gray-100 / gray-900
        }),
        singleValue: (base) => ({
            ...base,
            color: isDark ? '#f1f5f9' : '#111827',
        }),
    };
}
